import * as React from 'react'
import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'

type AIAssistantHistoryContainerProps = {
    children: React.ReactNode
    maxHeight?: string | number
    sx?: SxProps<Theme>
    autoScrollOnChildrenChange?: boolean
    onlyWhenNearBottom?: boolean
    nearBottomThreshold?: number
    smooth?: boolean
}

export const AIAssistantHistoryContainer = React.forwardRef<
    HTMLDivElement,
    AIAssistantHistoryContainerProps
>(
    (
        {
            children,
            maxHeight,
            sx,
            autoScrollOnChildrenChange = true,
            onlyWhenNearBottom = true,
            nearBottomThreshold = 120,
            smooth = true,
        },
        ref
    ) => {
        const localRef = React.useRef<HTMLDivElement | null>(null)
        React.useImperativeHandle(ref, () => localRef.current as HTMLDivElement)

        const scrollToBottom = React.useCallback(() => {
            const el = localRef.current
            if (!el) return
            const top = el.scrollHeight
            if (smooth) {
                el.scrollTo({ top, behavior: 'smooth' })
            } else {
                el.scrollTop = top
            }
        }, [smooth])

        const isNearBottom = React.useCallback(() => {
            const el = localRef.current
            if (!el) return true
            const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
            return remaining < nearBottomThreshold
        }, [nearBottomThreshold])

        React.useEffect(() => {
            if (!autoScrollOnChildrenChange) return
            requestAnimationFrame(() => {
                if (!localRef.current) return
                if (!onlyWhenNearBottom || isNearBottom()) {
                    scrollToBottom()
                }
            })
        }, [
            children,
            autoScrollOnChildrenChange,
            onlyWhenNearBottom,
            isNearBottom,
            scrollToBottom,
        ])

        return (
            <Box
                ref={localRef}
                sx={{
                    'position': 'relative',
                    'overflowY': 'auto',
                    'overflowX': 'hidden',
                    'maxHeight': maxHeight ?? '100%',
                    'scrollBehavior': smooth ? 'smooth' : 'auto',
                    '&::-webkit-scrollbar': { width: '8px' },
                    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        borderRadius: '8px',
                    },
                    'scrollbarWidth': 'thin',
                    'scrollbarColor': 'rgba(0,0,0,0.2) transparent',
                    ...(sx as object),
                }}
            >
                {children}
            </Box>
        )
    }
)

AIAssistantHistoryContainer.displayName = 'AIAssistantHistoryContainer'

export default AIAssistantHistoryContainer
